import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiUsers, FiTrendingUp, FiAward, FiCalendar } = FiIcons;

const About = () => {
  const milestones = [
    {
      year: '1998',
      title: 'Foundation',
      description: 'Founded by seasoned professionals during South Africa\'s economic transformation period',
      icon: FiUsers,
      color: 'bg-blue-500'
    },
    {
      year: '2000s',
      title: 'Expansion',
      description: 'Established partnerships with major JSE-listed entities and developed BEE expertise',
      icon: FiTrendingUp,
      color: 'bg-green-500'
    },
    {
      year: '2010',
      title: 'Recognition',
      description: 'Became recognised thought leaders in sustainable business transformation',
      icon: FiAward,
      color: 'bg-purple-500'
    },
    {
      year: '2024',
      title: 'Today',
      description: 'Offices across Gauteng with project delivery throughout Southern Africa',
      icon: FiTarget,
      color: 'bg-orange-500'
    }
  ];

  const values = [
    {
      icon: FiTarget,
      title: 'Strategic Excellence',
      description: 'We deliver comprehensive strategies that drive measurable business impact'
    },
    {
      icon: FiUsers,
      title: 'Local Expertise',
      description: 'Deep understanding of South African business landscape and regulatory environment'
    },
    {
      icon: FiTrendingUp,
      title: 'Sustainable Growth',
      description: 'Focus on long-term value creation for stakeholders and communities'
    },
    {
      icon: FiAward,
      title: 'Proven Results',
      description: 'Over two decades of successful transformations across diverse industries'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-900 to-purple-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 font-figtree">
              About RSA Solutions
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              Empowering South African enterprises with strategic clarity and operational excellence that creates lasting value for stakeholders, communities, and the broader economy.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company History */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8 font-figtree text-center">
              Our Story
            </h2>
            <div className="prose prose-lg max-w-none text-gray-600">
              <p className="mb-6 leading-relaxed">
                Founded in 1998 by seasoned professionals with extensive experience across mining, financial services, and manufacturing sectors, RSA Solutions emerged during South Africa's economic transformation period. Our founders recognised the unique opportunity to bridge international consulting methodologies with indigenous business acumen.
              </p>
              <p className="mb-6 leading-relaxed">
                The practice expanded significantly during the early 2000s, establishing strong partnerships with major JSE-listed entities whilst developing specialised expertise in Black Economic Empowerment (BEE) strategy and implementation. By 2010, we had become recognised thought leaders in sustainable business transformation.
              </p>
              <p className="leading-relaxed">
                Today, RSA Solutions maintains offices across Gauteng with project delivery capabilities throughout Southern Africa. Our client portfolio spans mining giants, emerging technology companies, retail chains, and government entities, united by our commitment to driving measurable business impact.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Timeline - New Design */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 font-figtree">
              Our Journey
            </h2>
            <p className="text-xl text-gray-600">
              Key milestones in our growth and development
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="relative"
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow h-full">
                  {/* Year Badge */}
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-bold font-figtree">
                      {milestone.year}
                    </div>
                  </div>

                  {/* Icon */}
                  <div className={`w-16 h-16 ${milestone.color} rounded-full flex items-center justify-center mx-auto mb-6 mt-4`}>
                    <SafeIcon icon={milestone.icon} className="w-8 h-8 text-white" />
                  </div>

                  {/* Content */}
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 font-figtree">
                      {milestone.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {milestone.description}
                    </p>
                  </div>

                  {/* Connecting Line (hidden on mobile) */}
                  {index < milestones.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-purple-200 transform -translate-y-1/2"></div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-purple-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-8 font-figtree">
              Our Mission
            </h2>
            <blockquote className="text-xl lg:text-2xl text-gray-200 italic max-w-4xl mx-auto leading-relaxed">
              "To empower South African enterprises with strategic clarity and operational excellence that creates lasting value for stakeholders, communities, and the broader economy."
            </blockquote>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 font-figtree">
              Our Values
            </h2>
            <p className="text-xl text-gray-600">
              The principles that guide everything we do
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-purple-200 transition-colors">
                  <SafeIcon icon={value.icon} className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 font-figtree">
                Expert Team, Local Insight
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Our team combines decades of international consulting experience with intimate knowledge of the South African business environment. We understand the unique challenges and opportunities that local enterprises face.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                From mining and manufacturing to financial services and technology, our consultants bring sector-specific expertise that ensures relevant, actionable recommendations for every engagement.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="text-3xl font-bold text-purple-900 mb-2 font-figtree">50+</div>
                  <div className="text-gray-600">Expert Consultants</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-purple-900 mb-2 font-figtree">15+</div>
                  <div className="text-gray-600">Industry Sectors</div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Professional team meeting"
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-purple-900/20 to-transparent rounded-lg"></div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;